import { useRef } from "react"
import { Link } from "react-router-dom"
import Logo from "./logo"
import { ArrowRight, Check } from "lucide-react"
import KeyFeatures from "./keyfeatures"
import BenefitsTabs from "./benefits-tabs"
import FAQTabs from "./faq-accordion"
import { useAuth } from "../contexts/auth-context"
import { useNavigate } from "react-router-dom"
import { useToast } from "../components/ui/use-toast"
// import heroImage from '../../assets/image.png';
import heroImage from '../assets/image.png';

export default function PatientCareLanding() {
  const abhaBenefitsRef = useRef<HTMLDivElement>(null)
  const { mobile } = useAuth() // Get mobile from auth context
  const navigate = useNavigate() // For navigation
  const { toast } = useToast() // For toast notifications

  // Handler for Book Appointment button
  const handleBookAppointment = () => {
    const storedMobile = localStorage.getItem("mobile");

    if (storedMobile) {
      // Mobile exists in localStorage, redirect to /doctors
      navigate("/doctors");
    } else {
      navigate("/doctors-list");
    }
  };

  return (
    <div className="antialiased text-gray-800 min-h-screen flex flex-col">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-10 flex w-full items-center justify-between px-6 py-3 border-b bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="flex items-center">
          <Logo />
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-8 md:pt-7 pb-16 md:pb-24 bg-gradient-to-br from-green-50 to-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center">
            {/* Left column: Text content */}
            <div className="w-full lg:w-1/2 lg:pr-12 mb-10 lg:mb-0">
              <span className="inline-block px-5 py-2 bg-gradient-to-r from-[#e6fffa] to-[#b2f5ea] text-[#008080] rounded-full text-sm font-medium font-roboto mb-6 shadow-lg border border-[#99f6e4] hover:shadow-xl hover:-translate-y-0.5 transform transition-all duration-300">
                Your Health, Your Control
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#008080] mb-6 leading-tight font-nunito">
                Your Health Journey, Simplified
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8 font-roboto leading-relaxed">
                Access your medical records, book appointments, and manage your family's health all in one secure
                platform.
              </p>

              {/* CTA button */}
              <div className="max-w-md w-full">
                <div className="flex flex-col space-y-4">
                  <button
                    onClick={handleBookAppointment}
                    className="w-full bg-[#008080] hover:bg-[#006666] text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50 font-roboto shadow-md hover:shadow-lg"
                  >
                    {/* Fixed structure for perfect centering */}
                    <div className="flex flex-col items-center justify-center text-center">
                      <span className="font-medium">Book appointment now!</span>
                      <span className="text-sm text-gray-100 mt-0.5">(In-clinic or Online)</span>
                    </div>
                  </button>

                  {/* Terms & Privacy */}
                  <div className="mb-5 mt-2 text-xs text-gray-600 text-center">
                    By continuing, you agree to&nbsp;
                    <Link
                      to="/terms-and-conditions"
                      className="text-teal-600 underline hover:text-teal-800 transition-colors font-medium"
                    >
                      T&C
                    </Link>
                    &nbsp;and{' '}
                    <Link
                      to="/privacy-policy"
                      className="text-teal-600 underline hover:text-teal-800 transition-colors font-medium"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </div>
                </div>
              </div>


              {/* Trust badges */}
              <div className="flex flex-wrap mt-8">
                <div className="flex items-center mr-6 mb-4">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-500" />
                  </div>
                  <span className="ml-2 text-sm text-gray-600 font-roboto">Secure & Private</span>
                </div>

                <div className="flex items-center mb-4">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-500" />
                  </div>
                  <span className="ml-2 text-sm text-gray-600 font-roboto">2-Minute Setup</span>
                </div>
              </div>
            </div>

            {/* Right column: Image */}
            <div className="w-full lg:w-1/2 relative">
              <div className="rounded-xl overflow-hidden shadow-lg relative z-1 bg-white p-2 transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
                <img
                  src={heroImage}
                  alt="Healthcare professionals in a hospital hallway with digital technology"
                  className="w-full h-auto rounded-lg"
                  onError={(e) => {
                    e.currentTarget.src = "https://via.placeholder.com/1080x720?text=Image+Not+Found"
                  }}
                />
                {/* <p className="text-xs text-gray-500 absolute bottom-3 right-3 bg-white bg-opacity-75 px-2 py-1 rounded font-roboto">
                  Photo by Luis Melendez
                </p> */}
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#008080]/10 rounded-full opacity-50 z-0 animate-pulse"></div>
              <div
                className="absolute -bottom-10 -left-10 w-40 h-40 bg-green-100 rounded-full opacity-50 z-0 animate-pulse"
                style={{ animationDelay: "1s" }}
              ></div>
            </div>
          </div>
        </div>
      </section>
      <BenefitsTabs />
      <KeyFeatures />
      <FAQTabs />
    </div>
  )
}
